/**
 * useSLATimer — reactive hook for tracking SLA time on active files.
 *
 * Provides a live-updating timer that recalculates active processing
 * time every second, accounting for paused durations. Returns current
 * active days, urgency level, and formatted display strings.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  calculateActiveTimeMs,
  calculateActiveDays,
  getSLAUrgency,
  isSLABreached,
} from '../utils/slaCalculator';

/**
 * @param {object} file — the file record to track
 * @param {number} intervalMs — update interval in milliseconds (default: 1000)
 * @returns {object} { activeDays, activeTimeMs, urgency, isBreached, formattedTime }
 */
export default function useSLATimer(file, intervalMs = 1000) {
  const [slaState, setSlaState] = useState(() => computeState(file));
  const intervalRef = useRef(null);

  const update = useCallback(() => {
    setSlaState(computeState(file));
  }, [file]);

  useEffect(() => {
    // Only run the timer for active files
    const shouldTick =
      file &&
      file.drawnAt &&
      ['in_progress', 'paused', 'sent_to_sign', 'signed'].includes(file.status);

    if (shouldTick) {
      update();
      intervalRef.current = setInterval(update, intervalMs);
    } else {
      update();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [file, intervalMs, update]);

  return slaState;
}

/**
 * Compute the current SLA state from a file record.
 */
function computeState(file) {
  if (!file || !file.drawnAt) {
    return {
      activeDays: 0,
      activeTimeMs: 0,
      urgency: 'green',
      isBreached: false,
      formattedTime: '0.0 days',
      progressPercent: 0,
    };
  }

  const activeTimeMs = calculateActiveTimeMs(file);
  const activeDays = calculateActiveDays(file);
  const urgency = getSLAUrgency(file);
  const isBreached = isSLABreached(file);

  // Format as days and hours
  let formattedTime;
  if (activeDays < 1) {
    const hours = Math.floor(activeDays * 24);
    const minutes = Math.floor((activeDays * 24 * 60) % 60);
    formattedTime = `${hours}h ${minutes}m`;
  } else {
    formattedTime = `${activeDays.toFixed(1)} days`;
  }

  // Progress as percentage of 3-day SLA
  const progressPercent = Math.min(100, (activeDays / 3) * 100);

  return {
    activeDays,
    activeTimeMs,
    urgency,
    isBreached,
    formattedTime,
    progressPercent,
  };
}
